import { createFileRoute, Outlet, useNavigate } from '@tanstack/react-router'
import { useEffect, useState } from 'react'
import { Loader2 } from 'lucide-react'
import { useUser } from '@/hooks/useUser'
import { AppShell } from '@/components/AppShell'
import ErrorFallback from '@/components/ErrorFallback'
import { WorkspaceProvider } from '@/api/workspace-context-provider'
import { AuthLoadingTimeout } from '@/auth/AuthLoadingTimeout'
import { useAuthLoadingWatchdog } from '@/auth/useAuthLoadingWatchdog'

// Logto's SDK briefly reports isAuthenticated=false during session rehydrate
// after a navigation. Wait for the state to be stable for this many ms before
// treating it as a real signed-out signal.
const AUTH_SETTLE_MS = 250

export const Route = createFileRoute('/_authenticated')({
  component: AuthenticatedLayout,
  errorComponent: ErrorFallback,
})

export function AuthenticatedLayout() {
  const { isLoading, isAuthenticated } = useUser()
  const navigate = useNavigate()
  const [confirmedSignedOut, setConfirmedSignedOut] = useState(false)

  // Logto wraps every SDK method in a proxy that flips its internal
  // loadingCount up at call-start and down at call-resolve. Every in-flight
  // getAccessToken() therefore toggles isLoading true→false, which would
  // otherwise remount this whole subtree (AppShell + Outlet + DashboardPage)
  // on every API call — losing refs/state and re-firing the call that caused
  // the toggle. Once we've seen isAuthenticated=true even once during this
  // mount, latch it so subsequent isLoading transitions are ignored. The
  // render-time set is React's "storing previous render information" pattern
  // — it triggers an immediate retry of the render without painting.
  const [hasEverAuthenticated, setHasEverAuthenticated] = useState(false)
  if (isAuthenticated && !hasEverAuthenticated) {
    setHasEverAuthenticated(true)
  }

  const isStableAuthenticated = isAuthenticated || hasEverAuthenticated

  useEffect(() => {
    if (isLoading || isAuthenticated || hasEverAuthenticated) {
      // Cancel any pending "confirmed signed-out" promotion; the synchronous
      // reset is intentional — we need to short-circuit a stale confirmed=true
      // before the timer below can re-promote on the next render.
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setConfirmedSignedOut(false)
      return
    }
    const timer = setTimeout(() => setConfirmedSignedOut(true), AUTH_SETTLE_MS)
    return () => clearTimeout(timer)
  }, [isLoading, isAuthenticated, hasEverAuthenticated])

  useEffect(() => {
    if (confirmedSignedOut) {
      navigate({ to: '/login' })
    }
  }, [confirmedSignedOut, navigate])

  // Bound the "Logto is rehydrating" spinner. The SDK has no fetch timeout on
  // its internal /oidc/token refresh, so a stalled IdP call leaves
  // isLoading=true forever — that's the "loads forever after idle" prod
  // regression. After the watchdog window we surface a recovery card.
  const authStuck = useAuthLoadingWatchdog(
    isLoading && !hasEverAuthenticated && !isAuthenticated,
  )
  if (authStuck) {
    return <AuthLoadingTimeout reason="session_timeout" />
  }

  // Initial boot: Logto hasn't told us yet whether we're signed in.
  if (isLoading && !hasEverAuthenticated) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    )
  }

  // Transient unauthed gap — wait for the 250ms debounce before redirecting.
  if (!isStableAuthenticated && !confirmedSignedOut) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    )
  }

  if (!isStableAuthenticated) {
    return null
  }

  return (
    <WorkspaceProvider>
      <AppShell>
        <Outlet />
      </AppShell>
    </WorkspaceProvider>
  )
}
